import React, { useState, useEffect } from 'react';
import Button from './Button';
import { API_BASE_URL } from '../config';
import './AddToSalesforceModal.css';

// Standard Salesforce Account.Industry picklist values, plus the few extras
// our NAICS classifier emits ("Real Estate", "Consulting"). Used as the
// datalist suggestions for the Industry field; users can also type a
// custom value if their org has a different picklist.
export const SF_INDUSTRY_OPTIONS = [
  'Agriculture', 'Apparel', 'Banking', 'Biotechnology', 'Chemicals',
  'Communications', 'Construction', 'Consulting', 'Education', 'Electronics',
  'Energy', 'Engineering', 'Entertainment', 'Environmental', 'Finance',
  'Food & Beverage', 'Government', 'Healthcare', 'Hospitality', 'Insurance',
  'Machinery', 'Manufacturing', 'Media', 'Not For Profit', 'Other',
  'Real Estate', 'Recreation', 'Retail', 'Shipping', 'Technology',
  'Telecommunications', 'Transportation', 'Utilities', 'Wholesale',
] as const;

// Friendly NAICS subsector labels — the Industry picker. MUST mirror
// backend/naics_classifier.py:SUBSECTORS (the backend is the source of truth
// that derives NAICS_Code__c / NAICS_Description__c / NAICS_Sector__c from the
// chosen label). 'Other' is the catch-all fallback.
export const SF_SUBSECTOR_OPTIONS = [
  'Security & Investigations', 'Repossession & Towing', 'Pest Control',
  'Landscaping', 'Cleaning Services', 'Trade Contractors', 'Property Management',
  'Parking', 'Hospitality', 'Trucking & Freight', 'Healthcare', 'Education',
  'Religious Organizations', 'Consulting', 'Government', 'Other',
] as const;

// Tokens kept fully uppercase when title-casing a screaming-caps name.
const NAME_KEEP_UPPER = new Set([
  // Legal/entity acronyms that stay uppercase. Suffix WORDS (Inc, Co, Ltd,
  // Corp) are intentionally NOT here — they should title-case ("INC" -> "Inc").
  'LLC', 'L.L.C.', 'LLP', 'PLLC', 'PC', 'P.C.',
  'USA', 'U.S.A.', 'US', 'HVAC', 'HOA', 'DBA', 'GPS', 'CCTV', 'ADT',
  'II', 'III', 'IV', 'TV', 'IT',
]);

// Title-case an ALL-CAPS account name (e.g. "SIDKO SECURITY INC" -> "Sidko
// Security Inc"). Leaves names that already contain lowercase letters untouched,
// so intentional casing ("McDonald's", "ADT Security") is preserved. Known
// acronyms/suffixes (LLC, USA, …) stay uppercase.
export function normalizeAccountName(raw: string): string {
  const name = (raw || '').trim();
  if (!name) return name;
  if (/[a-z]/.test(name)) return name;  // already mixed-case → respect it
  return name
    .split(/\s+/)
    .map((word) => {
      if (NAME_KEEP_UPPER.has(word.replace(/[^A-Za-z.]/g, '').toUpperCase())) {
        return word.toUpperCase();
      }
      // Capitalize the first letter overall and after each hyphen.
      return word.toLowerCase().replace(/(^|-)([a-z])/g, (_m, p1, p2) => p1 + p2.toUpperCase());
    })
    .join(' ');
}

// Strip path / query / hash / port from a website string so SF gets only the
// root URL (e.g. "https://www.security.com" instead of
// "https://www.security.com/products/foo?utm=bar"). Returns the original
// string when the input can't be parsed as a URL.
export function normalizeBaseUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    return `https://${parsed.hostname}`;
  } catch {
    return trimmed;
  }
}

// Hints shown under each Type tile so reps see what the disposition implies
// before saving. Centralized so the Add and Edit modals stay in sync.
export const SF_TYPE_HINTS: Record<string, string> = {
  New:             'No activity yet. A flow flips this to Prospect on first touch.',
  Prospect:        'Active outreach. A flow flips this to Customer when Net ACV > 0.',
  Customer:        'Set automatically by the Net ACV workflow.',
  Disqualified:    'Hidden from future Lead Finder results.',
  'Do Not Contact':'Hidden from future Lead Finder results.',
};

// Permissive US-address splitter mirroring backend/_parse_us_address. Best-effort:
// editable in the modal, server re-parses if these fields aren't supplied.
function parseAddressClient(formatted: string): {
  street?: string; city?: string; state?: string; zip?: string; country?: string;
} {
  if (!formatted) return {};
  // Optional comma between state and ZIP — Lead Finder's Google Places
  // addresses come through as "ST, ZIP", and without `,?` the ZIP slid into
  // the country slot (mirrors the same fix in backend/_ADDR_RE).
  const m = /^\s*(.+?),\s*([^,]+),\s*([A-Z]{2})\s*,?\s*(\d{5}(?:-\d{4})?)?\s*(?:,\s*(.+?))?\s*$/.exec(formatted);
  if (!m) return { street: formatted };
  return { street: m[1], city: m[2], state: m[3], zip: m[4], country: m[5] };
}

export interface AddToSalesforceBusiness {
  name: string;
  website?: string;
  phone?: string;
  address?: string;
  location?: { lat: number; lng: number };
  // Google Places signal — feeds the AE Notes prefill so reps see review
  // weight as starting context. Optional because saved-search reloads and
  // manual adds may not carry these.
  rating?: number;
  user_ratings_total?: number;
  // Days since the most recent Google review. Surfaced inline in the AE Notes
  // prefill so reps see "last review 87d ago" / "last review 2.3y ago" on the
  // new Account from day one — useful for re-engagement timing.
  last_review_age_days?: number;
  // Google Places business_status. Non-OPERATIONAL values get a flag in the
  // AE Notes prefill so the rep doesn't accidentally onboard a permanently-
  // closed shop without seeing the warning.
  business_status?: 'OPERATIONAL' | 'CLOSED_TEMPORARILY' | 'CLOSED_PERMANENTLY';
  // Company LinkedIn URL (from Apollo enrichment / homepage scrape / SF).
  // Written to Account.Company_LinkedIn__c on create.
  linkedin_url?: string;
  // Other socials + email scraped from the homepage; written to the matching
  // Account.Company_*__c fields on create.
  instagram_url?: string;
  facebook_url?: string;
  x_url?: string;
  yelp_url?: string;
  company_email?: string;
}

// Lightweight shape of a *sibling* location surfaced when the rep adds a
// franchise card with >1 location. Listed in the AE Notes prefill so the
// single SF Account captures every branch we found — reps spin off separate
// Accounts later as territory work begins.
export interface AddToSalesforceOtherLocation {
  name?: string;
  address?: string;
  phone?: string;
  rating?: number;
  user_ratings_total?: number;
}

/** Compact relative-time label matching the card's `last review …` suffix:
 *   today / 5d ago / 3w ago / 8mo ago / 2.3y ago. */
function relativeAge(days: number): string {
  if (days < 1) return 'today';
  if (days < 30) return `${days}d ago`;
  if (days < 90) return `${Math.round(days / 7)}w ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${(days / 365).toFixed(1)}y ago`;
}

// Build the standard AE Notes starting line from Google review signal.
// Format examples:
//   [2026-06-15] Google: 4.7★ (123 reviews · last 87d ago). [Summit]
//   [2026-06-15] Google: 4.5★ (8 reviews · last 2.3y ago) — ⚠ permanently closed per Google. [Summit]
//   [2026-06-15] Google: no rating data. [Summit]
// The last-review suffix renders only when we have age data; the business_status
// flag renders only for non-OPERATIONAL so the implicit healthy case stays clean.
export function formatAENotesPrefill(
  rating?: number,
  userRatingsTotal?: number,
  lastReviewAgeDays?: number,
  businessStatus?: 'OPERATIONAL' | 'CLOSED_TEMPORARILY' | 'CLOSED_PERMANENTLY',
): string {
  const date = new Date().toISOString().slice(0, 10);
  // Only attach the "last review" suffix when we have age data AND there are
  // actual reviews — saying "no reviews yet · last review 1y ago" contradicts
  // itself. Phrasing matches the card's `last review …` suffix.
  const hasReviews = !!(userRatingsTotal && userRatingsTotal > 0);
  const ageSuffix = (hasReviews && lastReviewAgeDays !== undefined && lastReviewAgeDays !== null)
    ? ` · last review ${relativeAge(lastReviewAgeDays)}`
    : '';
  let detail: string;
  if (hasReviews && rating !== undefined && rating !== null) {
    const label = userRatingsTotal === 1 ? 'review' : 'reviews';
    detail = `${rating}★ (${userRatingsTotal} ${label}${ageSuffix})`;
  } else if (userRatingsTotal === 0) {
    detail = 'no reviews yet';
  } else {
    detail = 'no rating data';
  }
  let statusNote = '';
  if (businessStatus === 'CLOSED_PERMANENTLY') {
    statusNote = ' — ⚠ permanently closed per Google';
  } else if (businessStatus === 'CLOSED_TEMPORARILY') {
    statusNote = ' — temporarily closed per Google';
  }
  return `[${date}] Google: ${detail}${statusNote}. [Summit]`;
}

// Extend the AE Notes prefill with an automated block listing the OTHER
// locations of a multi-location franchise card. For now (per product call),
// franchise adds collapse to a single SF Account and the other branches live
// here as a documented audit trail — reps spin off separate Accounts later as
// they pick up territories. Returns the base prefill unchanged when there
// are zero other locations.
function formatLocation(loc: AddToSalesforceOtherLocation): string {
  const addr = (loc.address || '').trim();
  const parts: string[] = [];
  if (addr) parts.push(addr);
  if (loc.rating !== undefined && loc.rating !== null && (loc.user_ratings_total ?? 0) > 0) {
    parts.push(`${loc.rating}★ (${loc.user_ratings_total})`);
  }
  if (loc.phone) parts.push(loc.phone);
  return `  - ${parts.join('  ·  ') || (loc.name || '(unnamed)')}`;
}
export function buildFranchiseAeNotes(
  primaryRating: number | undefined,
  primaryRatingsTotal: number | undefined,
  otherLocations: AddToSalesforceOtherLocation[] | undefined,
  lastReviewAgeDays?: number,
  businessStatus?: 'OPERATIONAL' | 'CLOSED_TEMPORARILY' | 'CLOSED_PERMANENTLY',
): string {
  const base = formatAENotesPrefill(primaryRating, primaryRatingsTotal,
                                    lastReviewAgeDays, businessStatus);
  if (!otherLocations || otherLocations.length === 0) return base;
  const lines = otherLocations.map(formatLocation).join('\n');
  return `${base}\n\nOther locations found via Summit (${otherLocations.length}):\n${lines}\n\n` +
         `Above branches were discovered as part of one franchise card. This Account ` +
         `represents the primary location; spin off separate Accounts per branch as ` +
         `territories are picked up.`;
}

export interface AddToSalesforceResult {
  id: string;
  url: string;
  matched_existing: boolean;
  existing_owner_name?: string;
  type?: string;
}

// User-pickable Account.Type values. "Customer" is intentionally NOT here —
// a Salesforce flow auto-flips Prospect → Customer when Net_ACV__c > 0, so
// reps shouldn't be setting it manually from the Lead Finder.
// "Nurture" = real ICP but not buying yet — keep warm, revisit later. Treated
// as a NON-blocked disposition (still shows in Lead Finder results so a rep can
// re-engage), and protected from the find-websites auto-DQ flow (see app.py's
// TYPE_LOCKED) so a transient dead-site doesn't silently flip Nurture → DQ.
export const SF_ACCOUNT_TYPE_OPTIONS = [
  'New',
  'Prospect',
  'Nurture',
  'Disqualified',
  'Do Not Contact',
] as const;
export type SfAccountType = typeof SF_ACCOUNT_TYPE_OPTIONS[number];

// Submitted payload — the parent fires the request so the modal can close
// instantly and the API call runs in the background with a toast.
export interface AddToSalesforcePayload {
  name: string;
  website?: string;
  phone?: string;
  type: SfAccountType;
  industry?: string;
  // NAICS specifics from the classifier. Sent only when the rep kept the
  // suggested subsector, so the backend preserves the precise code (e.g.
  // Locksmiths) rather than the subsector's representative default.
  naics_code?: string;
  naics_description?: string;
  billing_street?: string;
  billing_city?: string;
  billing_state?: string;
  billing_postal_code?: string;
  billing_country?: string;
  billing_latitude?: number;
  billing_longitude?: number;
  // Raw formatted address ("street, city, ST zip"). The bulk-add path sends
  // this and lets the backend's _parse_us_address split it into the explicit
  // billing_* keys above. The single-add modal sends explicit keys directly.
  address?: string;
  ae_notes?: string;
  company_linkedin?: string;
  company_instagram?: string;
  company_facebook?: string;
  company_x?: string;
  company_yelp?: string;
  company_email?: string;
  // Account.Tier__c — picklist values are '1' | '2' | '3'. Omit or empty = unset.
  tier?: string;
  // NumberOfEmployees source. The single-add path augments this from
  // business.apollo_data.employee_count just before POST (see ProspectingPage);
  // bulk takes the same approach via the BulkAddBusiness propagation below.
  // Backend reads this on both /create-account and /create-accounts-bulk.
  employee_count?: number;
}

// Account.Tier__c picklist values (Salesforce-confirmed).
export const SF_TIER_OPTIONS = ['1', '2', '3'] as const;
export type SfTier = typeof SF_TIER_OPTIONS[number];

interface Props {
  business: AddToSalesforceBusiness;
  ownerName: string;
  // Other locations from a multi-location franchise card. When non-empty,
  // their addresses + ratings get appended to the AE Notes prefill so the
  // single SF Account captures the full franchise context. Empty / undefined
  // for single-location cards (the prefill stays unchanged).
  otherLocations?: AddToSalesforceOtherLocation[];
  onClose: () => void;
  // Fire-and-forget. Parent issues the actual SF request so the modal can
  // close instantly — Salesforce REST patches take 1–3s and there's no
  // reason to block the rep on the round-trip.
  onSubmit: (payload: AddToSalesforcePayload, business: AddToSalesforceBusiness) => void;
}

const AddToSalesforceModal: React.FC<Props> = ({ business, ownerName, otherLocations, onClose, onSubmit }) => {
  const parsed = parseAddressClient(business.address || '');
  const [name, setName] = useState(normalizeAccountName(business.name || ''));
  const [website, setWebsite] = useState(normalizeBaseUrl(business.website || ''));
  const [linkedin, setLinkedin] = useState(business.linkedin_url || '');
  const [phone, setPhone] = useState(business.phone || '');
  const [street, setStreet] = useState(parsed.street || '');
  const [city, setCity] = useState(parsed.city || '');
  const [state, setState] = useState(parsed.state || '');
  const [zip, setZip] = useState(parsed.zip || '');
  const [country, setCountry] = useState(parsed.country || 'USA');
  const [type, setType] = useState<SfAccountType>('New');
  const [tier, setTier] = useState<string>('');
  // `industry` holds the chosen NAICS subsector label (written to Account.Industry).
  // The classifier prefills it on mount; the rep can change it. The hidden
  // naicsCode/naicsDescription carry the classifier's *specific* code so the
  // backend preserves it — but only while the rep keeps the suggested label.
  const [industry, setIndustry] = useState('');
  const [naicsCode, setNaicsCode] = useState<string | null>(null);
  const [naicsDescription, setNaicsDescription] = useState<string | null>(null);
  const [suggestedLabel, setSuggestedLabel] = useState<string | null>(null);
  const [industryLoading, setIndustryLoading] = useState(true);
  // Prefilled from Google review signal + (when present) the other franchise
  // locations + last-review age + business_status flag. Editable by the rep.
  const [aeNotes, setAeNotes] = useState<string>(
    () => buildFranchiseAeNotes(
      business.rating, business.user_ratings_total, otherLocations,
      business.last_review_age_days, business.business_status,
    ),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    fetch(`${API_BASE_URL}/api/prospecting/salesforce/classify-industry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: business.name || '',
        website: business.website || '',
      }),
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.industry) {
          setIndustry(d.industry);
          setSuggestedLabel(d.industry);
          setNaicsCode(d.naics_code || null);
          setNaicsDescription(d.naics_description || null);
        }
      })
      .catch(() => { /* network error → leave field empty */ })
      .finally(() => setIndustryLoading(false));
    return () => ctrl.abort();
  }, [business.name, business.website]);

  // Switching the picker away from the classifier's suggestion drops the
  // specific code so the backend falls back to the new subsector's default.
  const handleIndustryChange = (value: string) => {
    setIndustry(value);
    if (value !== suggestedLabel) {
      setNaicsCode(null);
      setNaicsDescription(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const cleanWebsite = normalizeBaseUrl(website);
    onSubmit({
      name: name.trim(),
      website: cleanWebsite || undefined,
      phone: phone.trim() || undefined,
      type,
      industry: industry.trim() || undefined,
      tier: tier || undefined,
      naics_code: naicsCode || undefined,
      naics_description: naicsDescription || undefined,
      billing_street: street.trim() || undefined,
      billing_city: city.trim() || undefined,
      billing_state: state.trim() || undefined,
      billing_postal_code: zip.trim() || undefined,
      billing_country: country.trim() || undefined,
      billing_latitude: business.location?.lat,
      billing_longitude: business.location?.lng,
      ae_notes: aeNotes.trim() || undefined,
      company_linkedin: linkedin.trim() || undefined,
      company_instagram: business.instagram_url || undefined,
      company_facebook: business.facebook_url || undefined,
      company_x: business.x_url || undefined,
      company_yelp: business.yelp_url || undefined,
      company_email: business.company_email || undefined,
    }, business);
    onClose();
  };

  return (
    <div className="add-sf-modal-backdrop" onClick={onClose}>
      <div className="add-sf-modal" onClick={(e) => e.stopPropagation()}>
        <header className="add-sf-modal-header">
          <h2>Add to Salesforce</h2>
          <button type="button" className="add-sf-modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <form onSubmit={handleSubmit} className="add-sf-modal-body">
          <label className="add-sf-field">
            <span>Name <span className="add-sf-required">*</span></span>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </label>
          <label className="add-sf-field">
            <span>Website</span>
            <input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onBlur={() => setWebsite(normalizeBaseUrl(website))}
              placeholder="https://www.example.com"
            />
          </label>
          <label className="add-sf-field">
            <span>Company LinkedIn</span>
            <input
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              placeholder="https://www.linkedin.com/company/..."
            />
          </label>
          <label className="add-sf-field">
            <span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>
          <label className="add-sf-field">
            <span>Billing Street</span>
            <input value={street} onChange={(e) => setStreet(e.target.value)} />
          </label>
          <div className="add-sf-field-row">
            <label className="add-sf-field add-sf-field-grow">
              <span>City</span>
              <input value={city} onChange={(e) => setCity(e.target.value)} />
            </label>
            <label className="add-sf-field add-sf-field-state">
              <span>State</span>
              <input value={state} onChange={(e) => setState(e.target.value)} maxLength={2} />
            </label>
            <label className="add-sf-field add-sf-field-zip">
              <span>Zip</span>
              <input value={zip} onChange={(e) => setZip(e.target.value)} />
            </label>
          </div>
          <label className="add-sf-field">
            <span>Country</span>
            <input value={country} onChange={(e) => setCountry(e.target.value)} />
          </label>

          <label className="add-sf-field">
            <span>Industry</span>
            <select
              className="add-sf-select"
              value={industry}
              onChange={(e) => handleIndustryChange(e.target.value)}
            >
              <option value="">{industryLoading ? 'Detecting…' : '— Select industry —'}</option>
              {SF_SUBSECTOR_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            {naicsCode && industry === suggestedLabel && (
              <small className="add-sf-naics-hint">
                NAICS {naicsCode}{naicsDescription ? ` · ${naicsDescription}` : ''}
              </small>
            )}
          </label>

          <label className="add-sf-field">
            <span>Tier</span>
            <select className="add-sf-select" value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="">— None —</option>
              {SF_TIER_OPTIONS.map(t => <option key={t} value={t}>Tier {t}</option>)}
            </select>
          </label>

          <label className="add-sf-field">
            <span>AE Notes</span>
            <textarea
              value={aeNotes}
              onChange={(e) => setAeNotes(e.target.value)}
              rows={3}
              placeholder="Starting context — Google review signal is prefilled."
            />
          </label>

          <div className="add-sf-field">
            <span>Type</span>
            <div className="sf-type-picker" role="radiogroup" aria-label="Salesforce Account Type">
              {SF_ACCOUNT_TYPE_OPTIONS.map((t) => (
                <button
                  type="button"
                  key={t}
                  role="radio"
                  aria-checked={type === t}
                  data-type={t}
                  className={`sf-type-option${type === t ? ' is-selected' : ''}`}
                  onClick={() => setType(t)}
                >
                  {t}
                  <span className="sf-type-option-hint">{SF_TYPE_HINTS[t]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="add-sf-meta">
            <div><span>Source</span><code>Summit</code></div>
            <div><span>Owner</span>{ownerName || 'current Salesforce user'}</div>
          </div>

          <p className="add-sf-warning">
            ⚠ This creates an Account in <strong>production Salesforce</strong>.
          </p>

          {error && <div className="add-sf-error">{error}</div>}

          <footer className="add-sf-modal-footer">
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button variant="primary" type="submit">Create Account</Button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default AddToSalesforceModal;
